import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, MapPin, Landmark, Building2, ArrowRight, Layers } from "lucide-react";
import { getGeo } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/home/ExploreByState";

function Column({ title, icon: Icon, items, activeId, onPick, loading }) {
  return (
    <div className="flex min-h-[320px] flex-1 flex-col overflow-hidden rounded-xl bg-white ring-1 ring-border">
      <div className="flex items-center gap-2 border-b border-border bg-parchment/70 px-4 py-3">
        <Icon className="h-4 w-4 text-oldglory-red" />
        <span className="text-xs font-bold uppercase tracking-widest text-navy"><T>{title}</T></span>
      </div>
      <div className="no-scrollbar flex-1 overflow-y-auto py-1">
        {loading && <div className="px-4 py-3 text-sm text-muted-foreground"><T>Loading…</T></div>}
        {!loading && items.length === 0 && <div className="px-4 py-3 text-sm text-muted-foreground/70"><T>Select from the left</T></div>}
        {items.map((it) => (
          <button
            key={it.geo_id}
            onClick={() => onPick(it)}
            className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${
              activeId === it.geo_id ? "bg-navy text-white" : "hover:bg-parchment"
            }`}
            data-testid={`drill-item-${it.geo_id}`}
          >
            <span className="font-medium">{it.name}</span>
            <ChevronRight className={`h-4 w-4 ${activeId === it.geo_id ? "text-white" : "text-muted-foreground/50"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

export function DrillDemo() {
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [cities, setCities] = useState([]);
  const [sel, setSel] = useState({ state: null, county: null, city: null });
  const [loading, setLoading] = useState({ c: false, city: false });

  useEffect(() => {
    getGeo("us").then((d) => setStates(d.children.map((c) => ({ ...c })))).catch(() => {});
  }, []);

  const pickState = async (s) => {
    setSel({ state: s, county: null, city: null });
    setCities([]);
    setLoading((l) => ({ ...l, c: true }));
    const d = await getGeo(s.geo_id);
    setCounties(d.children);
    setLoading((l) => ({ ...l, c: false }));
  };

  const pickCounty = async (c) => {
    setSel((s) => ({ ...s, county: c, city: null }));
    setLoading((l) => ({ ...l, city: true }));
    const d = await getGeo(c.geo_id);
    setCities(d.children);
    setLoading((l) => ({ ...l, city: false }));
  };

  const target = sel.city || sel.county || sel.state;

  return (
    <section id="drilldown" data-testid="drilldown" className="mx-auto max-w-[1500px] px-4 py-20">
      <SectionHeading kicker="Geography is the spine" title="Drill down through America" />
      <p className="-mt-4 mb-8 max-w-2xl text-muted-foreground">
        <T>From the nation to a single community — every level is a real entity in the USAPulse knowledge graph. Try it: pick a state, then a county, then a community.</T>
      </p>

      <div className="flex items-center gap-2 text-sm font-medium text-navy">
        <span className="rounded bg-navy px-2.5 py-1 text-white">United States</span>
        {sel.state && <><ChevronRight className="h-4 w-4 text-oldglory-red" /><span className="rounded bg-navy/10 px-2.5 py-1">{sel.state.name}</span></>}
        {sel.county && <><ChevronRight className="h-4 w-4 text-oldglory-red" /><span className="rounded bg-navy/10 px-2.5 py-1">{sel.county.name}</span></>}
        {sel.city && <><ChevronRight className="h-4 w-4 text-oldglory-red" /><span className="rounded bg-oldglory-red px-2.5 py-1 text-white">{sel.city.name}</span></>}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Column title="States & Territories" icon={Landmark} items={states} activeId={sel.state?.geo_id} onPick={pickState} />
        <Column title="Counties" icon={Building2} items={counties} activeId={sel.county?.geo_id} onPick={pickCounty} loading={loading.c} />
        <Column title="Communities" icon={MapPin} items={cities} activeId={sel.city?.geo_id} onPick={(c) => setSel((s) => ({ ...s, city: c }))} loading={loading.city} />
      </div>

      <AnimatePresence>
        {target && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-6 flex flex-col items-start justify-between gap-4 rounded-xl bg-navy p-6 text-white md:flex-row md:items-center"
          >
            <div className="flex items-start gap-4">
              <Layers className="mt-1 h-6 w-6 text-oldglory-red" />
              <div>
                <div className="font-display text-xl font-bold">{target.name}</div>
                {target.description && <p className="mt-1 max-w-2xl text-sm text-white/70">{target.description}</p>}
              </div>
            </div>
            <Link to={`/place${target.url_path}`} className="inline-flex shrink-0 items-center gap-2 rounded-md bg-oldglory-red px-6 py-3 font-semibold transition-colors hover:bg-oldglory-dark" data-testid="drill-view-profile">
              <T>View full profile</T> <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
